"use client";
import { ReactNode } from "react";

import Link from "next/link";

import { InformationCircleIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import Slider from "react-slick";

import Brands from "shared-lib/Brands";
import { ClickServer } from "shared-lib/clickserver";
import * as gtag from "shared-lib/gtag";
import { brand } from "shared-lib/helpers/envFunctions";
import {
  logStatsig,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
} from "shared-lib/helpers/statsigFunctions";
import { isMobile } from "shared-lib/helpers/utilityFunctions";

import Chevron from "@/components/icons/chevron";
import RecipeImage from "@/components/recipe/image";
import { getRecipeLink } from "@/lib/helpers/frontendFunctions";

import Tooltip from "../Tooltip";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type BrandProps = {
  bannerText: null | ReactNode | string;
  bannerStyle: string;
  beforeText: null | ReactNode | string;
  afterText: null | ReactNode | string;
  sectionStyle: string;
  roundImages: boolean;
};

const pseudoBgClasses = "absolute z-30 from-white from-40% h-16 w-16";
const commonArrowClasses =
  "h-7 w-7 z-50 cursor-pointer absolute top-1/2 -translate-y-1/2";

function NextArrow({
  onClick = () => {
    return;
  },
}) {
  return (
    <div
      className={clsx("right-0 top-0 bg-gradient-to-l", pseudoBgClasses)}
      onClick={onClick}
    >
      <Chevron className={clsx("right-0 top-1/2", commonArrowClasses)} />
    </div>
  );
}

function PrevArrow({
  onClick = () => {
    return;
  },
}) {
  return (
    <div
      className={clsx("bg-gradient-to-r", pseudoBgClasses)}
      onClick={onClick}
    >
      <Chevron className={clsx("top-1/2 rotate-180", commonArrowClasses)} />
    </div>
  );
}

function getBrandValues(): BrandProps {
  const defaultValues: BrandProps = {
    bannerText: "",
    bannerStyle: "",
    beforeText: null,
    afterText: null,
    sectionStyle: "",
    roundImages: false,
  };

  switch (brand.id) {
    case Brands.heartlandcooking:
      return {
        ...defaultValues,
        ...{
          bannerText: "🇺🇸 America's Top Recipes 🇺🇸",
          bannerStyle: "border-b border-[#737373] uppercase justify-center",
          sectionStyle: "border-t border-[#737373]",
        },
      };
    case Brands.easiereats:
      return {
        ...defaultValues,
        ...{
          bannerText: "Simply Delicious",
          bannerStyle: "text-white bg-theme-primary uppercase justify-center",
          sectionStyle: "",
        },
      };
    case Brands.cookingprofessionally:
      return {
        ...defaultValues,
        ...{
          bannerText: "Test Kitchen Approved",
          bannerStyle:
            "border-b border-theme-gray-200 gap-2 uppercase justify-center",
          beforeText: null,
          afterText: (
            <Tooltip
              above={!isMobile()}
              right={!isMobile()}
              text={
                <>
                  All recipes are created, tested, and published by our team
                  right here in the U.S.A.
                </>
              }
            >
              <InformationCircleIcon
                className="h-5 w-5 fill-theme-primary"
                aria-description="more information"
              />
            </Tooltip>
          ),
          sectionStyle: "border border-black rounded",
          roundImages: true,
        },
      };
    case Brands.commandcooking:
      return {
        ...defaultValues,
        ...{
          bannerText: (
            <span>
              More&nbsp;<span className="font-bold">Winning</span>&nbsp;Recipes
            </span>
          ),
          bannerStyle: "gap-5 justify-center",
          beforeText: (
            <hr className="border-px my-2 flex-grow border-theme-primary" />
          ),
          afterText: (
            <hr className="border-px my-2 flex-grow border-theme-primary" />
          ),
        },
      };
    case Brands.recipesdirect:
      return {
        ...defaultValues,
        ...{
          bannerText: "This Week's Top Recipes",
          bannerStyle:
            "bg-theme-secondary text-white justify-center border-b-2 border-theme-primary",
        },
      };
    case Brands.recipereader:
      return {
        ...defaultValues,
        ...{
          bannerText: "Browse more top-ranked recipes 🤓",
          bannerStyle: "border-b border-[#737373]",
        },
      };
    case Brands.mamascooking:
      return {
        ...defaultValues,
        ...{
          bannerText: "Mama's Picks",
          bannerStyle:
            "text-white bg-theme-primary gap-4 uppercase justify-center",
          beforeText: <hr className="border-px my-2 flex-grow border-white" />,
          afterText: <hr className="border-px my-2 flex-grow border-white" />,
        },
      };
    case Brands.lazycooking:
      return {
        ...defaultValues,
        ...{
          bannerText: "More Yummy Recipes 🐨",
          bannerStyle: "justify-center border-b border-[#737373]",
          sectionStyle: "border-y border-theme-secondary border-y-4",
          roundImages: true,
        },
      };
    case Brands.recipesaver:
      return {
        ...defaultValues,
        ...{
          bannerText: (
            <span>
              Saver<span className="text-theme-primary">$</span> Favorite
              Flavors
            </span>
          ),
          bannerStyle:
            "border-y border-[#737373] justify-center bg-theme-secondary bg-opacity-50",
        },
      };
    default:
      return defaultValues;
  }
}

export default function RecipeBar({
  bannerType = "",
  recipes = [] as Recipe[],
}) {
  const brandProps = getBrandValues();

  return (
    <section className={clsx(brandProps.sectionStyle, "mb-4 print:hidden")}>
      <div className={clsx("mb-2 flex items-center", brandProps.bannerStyle)}>
        {brandProps.beforeText}
        {brandProps.bannerText}
        {brandProps.afterText}
      </div>
      {/* @ts-expect-error Suddenly started complaining */}
      <Slider
        infinite={true}
        slidesToShow={1}
        slidesToScroll={2}
        initialSlide={0}
        arrows={true}
        nextArrow={<NextArrow />}
        prevArrow={<PrevArrow />}
        variableWidth={true}
        className="slider z-10 mb-2 h-16"
        responsive={[
          {
            breakpoint: 768,
            settings: {
              slidesToScroll: 2,
            },
          },
          {
            breakpoint: 600,
            settings: {
              slidesToScroll: 1,
            },
          },
        ]}
      >
        {recipes.map((recipe, index) => (
          <div
            key={`bar-${index}`}
            className="h-16 !w-[215px] border-r border-[#d9d9d9]"
          >
            <Link
              href={getRecipeLink(recipe)}
              onClick={() => {
                gtag.event({
                  action: TRACKING_EVENTS.promotional_carousel_click,
                  category: TRACKING_CATEGORIES.navigation,
                  label: index.toString(),
                });

                logStatsig(TRACKING_EVENTS.promotional_carousel_click, {
                  index: index.toString(),
                  banner_type: bannerType,
                });

                ClickServer.track(TRACKING_EVENTS.promotional_carousel_click, {
                  index: index.toString(),
                  banner_type: bannerType,
                });
              }}
              className="ml-2 flex h-16 flex-row"
            >
              <div
                className={clsx(
                  brandProps.roundImages
                    ? "flex h-16 w-16 min-w-[64px] items-center justify-center overflow-hidden rounded-full border"
                    : "",
                )}
              >
                <RecipeImage recipe={recipe} className="h-16 w-16" />
              </div>
              <span className="my-auto ml-2 text-left text-[15px]">
                {recipe.shortTitle}
              </span>
            </Link>
          </div>
        ))}
      </Slider>
    </section>
  );
}
